import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../../../components/ui/button';
import Input from '../../../components/ui/input';
import Icon from '../../../components/appIcon';
import axiosInstance from 'utils/axiosInstance';

const ForgotPasswordForm = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex?.test(email);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    if (!email?.trim()) {
      setError('Email address is required');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);

    try {
      // ✅ Call FastAPI backend
      const formData = new FormData();
      formData.append('email', email);

      const response = await axiosInstance.post('/employee/forgot-password', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      navigate("/reset-password", { state: { email } });
      setIsSubmitted(true);
    } catch (err) {
      console.error('Forgot password error:', err);
      setError(
        err.response?.data?.detail ||
        'Something went wrong. Please try again later.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendEmail = async () => {
    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('email', email);

      await axiosInstance.post('/employee/forgot-password', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      navigate("/reset-password", { state: { email } });
    } catch (err) {
      setError('Failed to resend email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="space-y-6">
        {/* Success Icon */}
        <div className="flex justify-center">
          <div className="flex items-center justify-center w-16 h-16 bg-success/10 rounded-full">
            <Icon name="Mail" size={32} className="text-success" />
          </div>
        </div>

        {/* Success Message */}
        <div className="text-center space-y-3">
          <h3 className="text-lg font-semibold text-foreground">
            Check Your Email
          </h3>
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              We've sent password reset instructions to:
            </p>
            <p className="text-sm font-medium text-foreground bg-muted px-3 py-2 rounded-md">
              {email}
            </p>
          </div>
        </div>

        {/* Instructions */}
        <div className="bg-muted/50 border border-border rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Icon name="Info" size={20} className="text-primary mt-0.5" />
            <div className="flex-1 space-y-2">
              <h4 className="text-sm font-medium text-foreground">Next Steps:</h4>
              <ul className="text-xs text-muted-foreground space-y-1">
                <li>• Check your email inbox for the OTP or reset link</li>
                <li>• Don’t forget to check your spam/junk folder</li>
                <li>• The OTP will expire in 15 minutes for security</li>
                <li>• Use it to create a new password</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Resend Option */}
        <div className="text-center space-y-3">
          <p className="text-xs text-muted-foreground">Didn’t receive the email?</p>
          <Button
            variant="outline"
            size="sm"
            onClick={handleResendEmail}
            loading={isLoading}
            iconName="RefreshCw"
            iconPosition="left"
          >
            Resend Email
          </Button>
        </div>

        {/* Back to Login */}
        <div className="text-center pt-4 border-t border-border">
          <Link
            to="/login"
            className="inline-flex items-center space-x-2 text-sm text-primary hover:text-primary/80 enterprise-transition"
          >
            <Icon name="ArrowLeft" size={16} />
            <span>Back to Login</span>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Email Input */}
      <div className="space-y-4">
        <Input
          label="Email Address"
          type="email"
          placeholder="Enter your registered email"
          value={email}
          onChange={(e) => setEmail(e?.target?.value)}
          error={error}
          required
          disabled={isLoading}
        />
      </div>

      {/* Submit Button */}
      <Button
        type="submit"
        variant="default"
        size="lg"
        fullWidth
        loading={isLoading}
        iconName="Mail"
        iconPosition="left"
      >
        Send Reset Link
      </Button>

      {/* Help Text */}
      <div className="bg-muted/50 border border-border rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Icon name="HelpCircle" size={20} className="text-primary mt-0.5" />
          <div className="flex-1">
            <h4 className="text-sm font-medium text-foreground mb-1">Need Help?</h4>
            <p className="text-xs text-muted-foreground">
              If you don’t receive the email within 5 minutes, please check your
              spam folder or contact your system administrator for assistance.
            </p>
          </div>
        </div>
      </div>

      {/* Back to Login */}
      <div className="text-center pt-4 border-t border-border">
        <Link
          to="/login"
          className="inline-flex items-center space-x-2 text-sm text-primary hover:text-primary/80 enterprise-transition"
        >
          <Icon name="ArrowLeft" size={16} />
          <span>Back to Login</span>
        </Link>
      </div>
    </form>
  );
};

export default ForgotPasswordForm;
